"use client";

import { Trans } from "@lingui/react/macro";
import { AlertTriangleIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLocalStorage } from "@/lib/storage/use-local-storage";
import { cn } from "@/lib/utils";
import { WorkspacePaymentIssueContent } from "./workspace-payment-issue-content";

const DISMISS_KEY_PREFIX = "amethyst:workspace-payment-issue-dialog";

type WorkspacePaymentIssueDialogProps = {
  workspaceId: string;
  issueVersion: string;
  severity: "WARNING" | "ERROR";
};

export function WorkspacePaymentIssueDialog({
  workspaceId,
  issueVersion,
  severity,
}: WorkspacePaymentIssueDialogProps) {
  const [isMounted, setIsMounted] = useState(false);
  const [dismissedVersion, setDismissedVersion] = useLocalStorage<string>(
    `${DISMISS_KEY_PREFIX}:${workspaceId}`,
    {
      defaultValue: "",
      validate: (value): value is string => typeof value === "string",
    },
  );

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const dismiss = () => {
    setDismissedVersion(issueVersion);
  };

  if (!isMounted || dismissedVersion === issueVersion) {
    return null;
  }

  const isError = severity === "ERROR";

  return (
    <Dialog open onOpenChange={(open) => !open && dismiss()}>
      <DialogContent
        className="sm:max-w-lg"
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangleIcon
              className={cn(
                "size-5",
                isError
                  ? "text-destructive"
                  : "text-amber-600 dark:text-amber-400",
              )}
            />
            <DialogTitle>
              <Trans>Payment notice</Trans>
            </DialogTitle>
          </div>
          <DialogDescription asChild>
            <div className="pt-3 text-left text-sm text-foreground">
              <WorkspacePaymentIssueContent className="text-foreground" />
            </div>
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="sm:justify-end">
          <Button type="button" variant="outline" onClick={dismiss}>
            <Trans>Close</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
